import { Temporal } from '@js-temporal/polyfill';
import { DateParts, DateTimeParts, TimeParts } from '../../../types';
import { START_OF_DAY_TIME_PARTS } from '../../../constants/timeConstants';

function toTemporalPlainTime(time: TimeParts): Temporal.PlainTime {
  return Temporal.PlainTime.from(
    {
      hour: time.hours,
      minute: time.minutes,
      second: time.seconds ?? 0,
      millisecond: time.milliseconds ?? 0,
    },
    {
      overflow: 'reject',
    },
  );
}
const createDateTime = (timeZone: string) => (dateParts: DateParts) => (timeParts: TimeParts): Date | undefined => {
  try {
    const plainDate = Temporal.PlainDate.from(
      {
        year: dateParts.year!,
        month: dateParts.month! + 1, // Adjust from 0-based to 1-based month
        day: dateParts.day!,
      },
      {
        overflow: 'reject',
      },
    );
    const zoned = plainDate.toZonedDateTime({
      timeZone,
      plainTime: Temporal.PlainTime.from(toTemporalPlainTime(timeParts)),
    });

    // Convert Temporal.Instant to native Date for Intl formatting,
    // since Intl.DateTimeFormat currently only accepts Date objects
    // and does not support Temporal types in the standard or TypeScript bindings.
    const instant = zoned.toInstant();
    return new Date(instant.epochMilliseconds);
  } catch (error) {
    return undefined;
  }
};

export const buildInLocalTimezone = createDateTime(Temporal.Now.timeZoneId());
export const buildInUtcTimezone = createDateTime('UTC');

export function createLocalStartOfDay(dateParts: DateParts): Date | undefined {
  return buildInLocalTimezone(dateParts)(START_OF_DAY_TIME_PARTS);
}
export function createUtcStartOfDay(dateParts: DateParts): Date | undefined {
  return buildInLocalTimezone(dateParts)(START_OF_DAY_TIME_PARTS);
}
export function createProvidedTimezoneStartOfDay(dateParts: DateParts, timezone?: string): Date | undefined {
  if (!timezone) {
    return undefined;
  }
  return createDateTime(timezone)(dateParts)(START_OF_DAY_TIME_PARTS);
}
export function createDateTimeInUTC(dateTimeParts: DateTimeParts): Date | undefined {
  const { year, month, day, hours, minutes, seconds, milliseconds } = dateTimeParts;

  const dateParts: DateParts = { year, month, day };
  const timeParts: TimeParts = { hours, minutes, seconds, milliseconds };

  return buildInUtcTimezone(dateParts)(timeParts);
}

export function createDateTimeInLocal(dateTimeParts: DateTimeParts): Date | undefined {
  const { year, month, day, hours, minutes, seconds, milliseconds } = dateTimeParts;

  const dateParts: DateParts = { year, month, day };
  const timeParts: TimeParts = { hours, minutes, seconds, milliseconds };

  return buildInLocalTimezone(dateParts)(timeParts);
}

export function createDateTimeInProvidedTimezone(dateTimeParts: DateTimeParts, timeZone: string): Date | undefined {
  const { year, month, day, hours, minutes, seconds, milliseconds } = dateTimeParts;

  const dateParts: DateParts = { year, month, day };
  const timeParts: TimeParts = { hours, minutes, seconds, milliseconds };

  return createDateTime(timeZone)(dateParts)(timeParts);
}

export function isValidDate(date: DateParts) {
  try {
    Temporal.PlainDate.from(
      {
        year: date.year!,
        month: date.month! + 1,
        day: date.day!,
      },
      { overflow: 'reject' },
    );
    return true;
  } catch {
    return false;
  }
}

export function isValidDateTime(date: DateParts, time: TimeParts, timeZone: string) {
  return !!buildInUtcTimezone(date)(time);
}

export function isValidTime(time: TimeParts) {
  if (Object.keys(time).length === 0) return false; // empty object
  const now = Temporal.Now.plainDateISO();
  const dateParts: DateParts = {
    year: now.year,
    month: now.month - 1,
    day: now.day,
  };
  return !!buildInUtcTimezone(dateParts)(time);
}
